// **SUPPLIER ARTICLE TYPES**

// **ARTICLE UNIT INTERFACE**
export interface ArticleUnit {
  _value: string;
  articleId: string;
  articleNumber: string;
  product: string;
  markantUnitDescriptor_DE: string;
  wedlVerpackungseinheit_DE?: string;
  markantQuantityOfBaseUnits?: number;
  wedlEinkaufEingabeEinheit_DE?: string;
  wedlVerkaufEingabeEinheit_DE?: string;
  relStkGewichtsartikel?: string;
  markantIsTradeItemABaseUnit?: boolean;
  markantWidth?: number;
  markantWidthUom_DE?: string;
  markantHeight?: number;
  markantHeightUom_DE?: string;
  markantDepth?: number;
  markantDepthUom_DE?: string;
  markantGrossWeight?: number;
  markantGrossWeightUom_DE?: string;
  volumeCM3?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// **ARTICLE BARCODE INTERFACE**
export interface ArticleBarcode {
  _value: string;
  articleId: string;
  articleNumber: string;
  product: string;
  markantGtin: string;
  markantUnitDescriptor_DE: string;
  barcodeType: 'EAN13' | 'EAN8' | 'UPC' | 'CODE128' | 'GTIN14' | 'OTHER';
  isMainBarcode: boolean;
  isPrintable: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// **LMIV INTERFACE**
export interface ArticleLMIV {
  _value: string;
  articleId: string;
  articleNumber: string;
  product: string;
  foodName_DE: string;
  foodName_EN?: string;
  ingredients_DE?: string;
  ingredients_EN?: string;
  allergens_DE?: string;
  allergens_EN?: string;
  energyKJ?: number;
  energyKcal?: number;
  fatTotal?: number;
  fatSaturated?: number;
  carbohydratesTotal?: number;
  carbohydratesSugars?: number;
  protein?: number;
  salt?: number;
  fiber?: number;
  sodium?: number;
  netWeight?: number;
  netWeightUnit?: string;
  bestBeforeType: 'MHD' | 'VERBRAUCHSDATUM';
  storageConditions_DE?: string;
  storageConditions_EN?: string;
  countryOfOrigin_DE?: string;
  countryOfOrigin_EN?: string;
  manufacturerName?: string;
  manufacturerAddress?: string;
  alcoholContent?: number;
  warnings_DE?: string;
  warnings_EN?: string;
  isActive: boolean;
  lastUpdated: string;
  createdBy: string;
  updatedBy?: string;
}

// **NUTRITIONAL VALUES INTERFACE**
export interface ArticleNutritionalValues {
  _value: string;
  articleId: string;
  articleNumber: string;
  product: string;
  energyKJ: number;
  energyKcal: number;
  fatTotal: number;
  fatSaturated: number;
  fatMonounsaturated?: number;
  fatPolyunsaturated?: number;
  carbohydratesTotal: number;
  carbohydratesSugars: number;
  carbohydratesPolyols?: number;
  carbohydratesStarch?: number;
  fiber: number;
  protein: number;
  salt: number;
  vitaminA?: number;
  vitaminD?: number;
  vitaminE?: number;
  vitaminK?: number;
  vitaminC?: number;
  thiamin?: number;
  riboflavin?: number;
  niacin?: number;
  vitaminB6?: number;
  folate?: number;
  vitaminB12?: number;
  biotin?: number;
  pantothenicAcid?: number;
  potassium?: number;
  chloride?: number;
  calcium?: number;
  phosphorus?: number;
  magnesium?: number;
  iron?: number;
  zinc?: number;
  copper?: number;
  manganese?: number;
  fluoride?: number;
  selenium?: number;
  chromium?: number;
  molybdenum?: number;
  iodine?: number;
  caffeine?: number;
  taurine?: number;
  cholesterol?: number;
  referenceAmount: number;
  referenceUnit: 'g' | 'ml' | 'portion';
  portionSize?: number;
  portionUnit?: string;
  analysisMethod: 'ANALYZED' | 'CALCULATED' | 'DATABASE';
  analysisDate?: string;
  laboratoryName?: string;
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  verifiedBy?: string;
  verificationDate?: string;
  verificationNotes?: string;
  isActive: boolean;
  version: number;
  createdBy: string;
  updatedBy?: string;
}

export interface LMIVData {
  ingredients: string;
  allergens: string[];
  nutritionalValues: {
    energy: { kj?: number; kcal?: number };
    fat: { total?: number; saturated?: number };
    carbohydrates: { total?: number; sugars?: number };
    fiber?: number;
    protein?: number;
    salt?: number;
  };
  additives: string[];
  origin?: string;
  storage?: string;
  usageInstructions?: string;
}

export enum ArticleStatus {
  DRAFT = 'draft',                    // Entwurf - Artikel existiert, aber noch nicht an Lieferant gesendet
  NEW = 'new',                        // Neu - Von Lieferant neu erstellt, wartet auf Admin-Review
  REQUESTED = 'requested',            // Angefordert - An Lieferant gesendet, wartet auf Bearbeitung (Admin sieht "Angefordert", Lieferant sieht "Ausstehend")
  IN_PROGRESS = 'in_progress',        // In Bearbeitung - Lieferant arbeitet daran
  SUBMITTED = 'submitted',            // Eingereicht - Lieferant hat fertig bearbeitet, wartet auf Ihre Genehmigung
  APPROVED = 'approved',              // Genehmigt - Von Ihrer Seite akzeptiert
  COMPLETED = 'completed',            // Vollständig - Komplett abgeschlossen
  REJECTED = 'rejected',              // Abgelehnt - Von Ihrer Seite abgelehnt
  NEEDS_CORRECTION = 'needs_correction' // Korrektur erforderlich - Zurück an Lieferant zur Überarbeitung
}

export interface ChangeLogEntry {
  field: string;
  fieldLabel?: string;
  oldValue?: any;
  newValue?: any;
  changedBy: string;
  changedAt: string;
  comment?: string;
  changeType?: 'created' | 'updated' | 'deleted';
  isVisible?: boolean; // Ob die Änderung im UI hervorgehoben werden soll
}

export interface InternalNote {
  note: string;
  createdBy: string;
  createdAt: string;
  isVisibleToSupplier?: boolean;
}

export interface SupplierNote {
  note: string;
  createdBy: string;
  createdAt: string;
}

// Excel Import Interfaces
export interface ExcelImportData {
  // **GRUNDDATEN**
  _Value: string; // Artikelnummer (erforderlich)
  name?: string; // Name - aus verschiedenen Quellen gemappt
  description?: string; // Beschreibung
  brand?: string; // Marke
  
  // **WEDL BEZEICHNUNGEN**
  wedlDesignation1?: string; // WEDL Bezeichnung 1
  wedlDesignation2?: string; // WEDL Bezeichnung 2
  
  // **IDENTIFIKATION & CODES**
  gtin?: string; // GTIN
  itemNumber?: string; // Item Number
  externalArticleNumber?: string; // Externe Artikelnummer
  supplierAssignedId?: string; // Lieferanten ID (legacy)
  intrastatNumber?: string; // Intrastat
  creditorNumber?: string; // Kreditor-Nr.
  
  // **INHALT & EINHEITEN - KORREKTE DB-FELDNAMEN**
  wedlContent?: number; // WEDL Inhalt (Number)
  wedlContentUnit?: string; // WEDL Inhaltseinheit
  wedlUnit?: string; // WEDL Werbeeinheit
  pieceWeightArticle?: boolean; // Stückgewichtsartikel
  
  // **LINIE**
  wedlLine?: string; // WEDL Linie
  
  // **NETTO-INHALT**
  netContent?: number; // Netto-Inhalt
  netContentUom?: string; // Netto-Inhaltseinheit
  drainedWeight?: number; // Abtropfgewicht
  drainedWeightUom?: string; // Abtropfgewicht Einheit
  
  // **HERSTELLER-INFO**
  manufacturerName?: string; // Herstellername
  manufacturerGLN?: string; // Hersteller GLN
  contactName?: string; // Inverkehrbringen Name
  contactAddress?: string; // Inverkehrbringen Adresse
  
  // **LIEFERANTEN-INFO**
  mainSupplierCode?: string; // Hauptlieferant Code
  mainSupplierGLN?: string; // Hauptlieferant GLN
  supplierContact?: string; // Lieferant Kontakt
  
  // **GEFAHRGUT**
  dangerousGoods?: string; // Gefahrgut
  
  // **LAUFZEITEN & HALTBARKEIT**
  minTermIncoming?: string; // Min. Lauftage Eingang
  minTermOutgoing?: string; // Min. Lauftage Ausgang
  shelfLifeDays?: number; // Haltbarkeit in Tagen
  
  // **TEMPERATUR**
  stockTempMin?: number; // Min. Temperatur
  stockTempMax?: number; // Max. Temperatur
  
  // **KATEGORIEN & CODES**
  departmentCode?: string; // Abteilungscode
  storageZone?: string; // Lagerzone
  
  // **HERKUNFT**
  countryOfOrigin?: string; // Herkunftsland
  originCountryTrue?: string; // Herkunftsland (true)
  
  // **MEHRWEG/EINWEG**
  reusableCode?: string; // Mehrweg/Einweg
  
  // **ECR CODES**
  ecrLevel1?: string; // ECR Level 1
  ecrLevel2?: string; // ECR Level 2
  ecrCode?: string; // ECR Code
  
  // **LABELS**
  labels?: string[] | string; // Labels (Array oder komma-separierter String)
  
  // **ALKOHOL**
  alcoholContent?: number; // Alkoholgehalt
  
  // **FISCH-SPEZIFISCHE FELDER**
  fangzone?: string; // Fangzone
  fangmethode?: string; // Fangmethode
  scientificName?: string; // Lateinischer Name
  productionMethod?: string; // Produktionsmethode
  thawingInstructions?: string; // Auftauhinweis
  
  // **GRUNDPREIS**
  basePriceContent?: string; // Grundpreisinhalt
  baseUnit?: string; // Grundeinheit
  baseComparisonUnitCode?: string; // Basis Vergleichs-Einheitencode
  
  // **VERKAUFSPREIS**
  salesPrice?: number; // VK-Preis
  
  // **LEGACY FELDER (für Rückwärtskompatibilität)**
  '#BCItemDescription'?: string; // Name (primär, legacy)
  'BCItemDescription'?: string; // Name (legacy)
  'WEDLBezeichnung1_DE'?: string; // Name (legacy)
  'WEDLBezeichnung2_DE'?: string; // Beschreibung 2 (legacy)
  'BrandName'?: string; // Marke (legacy)
  'MarkantbaseUnitGtin'?: string; // GTIN (legacy)
  '#ItemNumber'?: string; // Item Number (legacy)
  'MarkantsupplierAssignedIdentNumber'?: string; // Externe Artikelnummer (legacy)
  'MarkantIntrastatNumber'?: string; // Intrastat (legacy)
  '#RelWEDLHauptlieferantKreditorNr'?: string; // Kreditor-Nr. (legacy)
  'WEDLInhalt'?: number; // Inhalt (legacy)
  'content'?: number | string; // Inhalt (legacy)
  'WEDLInhaltEinheit_DE'?: string; // Inhaltseinheit (legacy)
  'contentUnit'?: string; // Inhaltseinheit (legacy)
  'WEDLEinheit_DE'?: string; // Werbeeinheit (legacy)
  'unit'?: string; // Werbeeinheit (legacy)
  'WEDLLinie_DE'?: string; // Linie (legacy)
  'line'?: string; // Linie (legacy)
  'MarkantnetContent'?: number; // Netto-Inhalt (legacy)
  'MarkantnetContentUom_DE'?: string; // Netto-Inhaltseinheit (legacy)
  'MarkantdrainedWeight'?: number; // Abtropfgewicht (legacy)
  'MarkantdrainedWeightUom_DE'?: string; // Abtropfgewicht Einheit (legacy)
  'MarkantmanufacturerName'?: string; // Herstellername (legacy)
  'MarkantmanufacturerGLN'?: string; // Hersteller GLN (legacy)
  'MarkantcontactName'?: string; // Inverkehrbringen Name (legacy)
  'MarkantcontactAddress'?: string; // Inverkehrbringen Adresse (legacy)
  'WEDLSupplierCodeHauptlieferant'?: string; // Hauptlieferant Code (legacy)
  '#RELWEDLHauptlieferantGLN'?: string; // Hauptlieferant GLN (legacy)
  '#RELWEDLLieferantang'?: string; // Lieferant Kontakt (legacy)
  'MarkantdangerousGoodsRegulation'?: string; // Gefahrgut (legacy)
  'LMIVMarkantMinTermIncomming'?: string; // Min. Lauftage Eingang (legacy)
  '#LMIVMarkantMinTermOutcoming'?: string; // Min. Lauftage Ausgang (legacy)
  'GHPA00HBK'?: number; // Haltbarkeit in Tagen (legacy)
  'LMIVMarkantStockTempMin'?: number; // Min. Temperatur (legacy)
  'LMIVMarkantStockTempMax'?: number; // Max. Temperatur (legacy)
  'WEDLABTABTKZWGRKZ'?: string; // Abteilungscode (legacy)
  'WEDLLagerzone_DE'?: string; // Lagerzone (legacy)
  'MarkantCountryOfOrigin'?: string; // Herkunftsland (legacy)
  'WEDLHerkunftslandtrue'?: string; // Herkunftsland (true, legacy)
  'MarkantonewayReusableCode_DE'?: string; // Mehrweg/Einweg (legacy)
  '#RELWEDLECRECRSGECRSUGECRWGREbene1'?: string; // ECR Level 1 (legacy)
  '#RELWEDLECRECRSGECRSUGECRWGREbene2'?: string; // ECR Level 2 (legacy)
  'WEDLLabels_DE'?: string; // Labels (legacy)
  'LMIVAlcoholContent'?: number; // Alkoholgehalt (legacy)
  'Fangzone'?: string; // Fangzone (legacy)
  'Fangmethode'?: string; // Fangmethode (legacy)
  'WEDLWissenschaftlicherArtname'?: string; // Lateinischer Name (legacy)
  'WEDLProduktionsmethode'?: string; // Produktionsmethode (legacy)
  'WEDLAuftauhinweis'?: string; // Auftauhinweis (legacy)
  'bcItemDescription'?: string; // Name (lowercase, legacy)
  'verkaufspreis'?: number; // VK-Preis (deutsch, legacy)
}

export interface ImportResult {
  successful: number;
  failed: number;
  errors: Array<{
    row: number;
    articleNumber: string;
    error: string;
  }>;
}

export interface RequestedField {
  field: string;
  requestedBy: string;
  requestedAt: string;
  comment?: string;
  isResolved: boolean;
}

// **NEUE TABELLEN-INTERFACES**

export interface IUnit {
  _id: string;
  code: string;
  name_DE: string;
  name_EN?: string;
  category: 'content' | 'weight' | 'volume' | 'piece' | 'other';
  baseUnit?: string;
  conversionFactor?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IBarcode {
  _id: string;
  articleId: string;
  articleNumber: string;
  barcodeType: 'EAN13' | 'EAN8' | 'UPC' | 'CODE128' | 'OTHER';
  barcodeValue: string;
  isPrimary: boolean;
  isActive: boolean;
  description?: string;
  validFrom?: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IECRCategory {
  _id: string;
  articleId: string;
  articleNumber: string;
  ecrCode: string;
  level1: string;
  level2: string;
  level3?: string;
  description_DE: string;
  description_EN?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ILabel {
  _id: string;
  articleId: string;
  articleNumber: string;
  labelName: string;
  labelType: 'QUALITY' | 'ORGANIC' | 'REGIONAL' | 'ALLERGEN' | 'CERTIFICATION' | 'OTHER';
  description?: string;
  iconUrl?: string;
  validFrom?: string;
  validTo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface INutritionalValue {
  _id: string;
  articleId: string;
  articleNumber: string;
  nutrientName: string;
  nutrientType: 'ENERGY_KJ' | 'ENERGY_KCAL' | 'FAT' | 'FAT_SATURATED' | 'CARBOHYDRATES' | 'CARBOHYDRATES_SUGARS' | 'FIBER' | 'PROTEIN' | 'SALT' | 'OTHER';
  value: number;
  unit: string;
  per100g: boolean;
  measurementType: 'PER_100G' | 'PER_PORTION' | 'PER_PACKAGE';
  gtin?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IAllergen {
  _id: string;
  articleId: string;
  articleNumber: string;
  allergenName: string;
  allergenCode?: string;
  containsAllergen: boolean;
  mayContainAllergen: boolean;
  allergenType: 'GLUTEN' | 'CRUSTACEANS' | 'EGGS' | 'FISH' | 'PEANUTS' | 'SOYBEANS' | 'MILK' | 'NUTS' | 'CELERY' | 'MUSTARD' | 'SESAME' | 'SULPHITES' | 'LUPIN' | 'MOLLUSCS' | 'OTHER';
  description?: string;
  gtin?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierArticle {
  _id: string;
  
  // Basic Info
  articleNumber: string;
  supplierArticleNumber?: string;
  supplierId: string | {
    _id: string;
    name: string;
    email: string;
    contactPerson?: string;
  } | null;
  
  // Article Data
  name: string;
  description?: string;
  category?: string;
  
  // Excel Import Fields
  description2?: string; // WEDLBezeichnung2_DE
  content?: string; // WEDLInhalt - kann komplexe Werte wie "2 x 40", "ca. 30" enthalten
  contentUnit?: string; // WEDLInhaltEinheit_DE
  unit?: string; // WEDLEinheit_DE
  itemNumber?: string; // #ItemNumber
  supplierAssignedId?: string; // MarkantsupplierAssignedIdentNumber
  intrastatNumber?: string; // MarkantIntrastatNumber
  mainSupplierCode?: string; // WEDLSupplierCodeHauptlieferant
  mainSupplierGLN?: string; // #RELWEDLHauptlieferantGLN
  supplierContact?: string; // #RELWEDLLieferantang
  supplierCodeId?: string; // #RelWEDLSupplierCodeHauptlieferantSupplierCodeID
  
  // **NEUE EINKAUF FELDER**
  externalArticleNumber?: string; // MarkantsupplierAssignedIdentNumber
  manufacturerName?: string; // MarkantmanufacturerName
  manufacturerGLN?: string; // MarkantmanufacturerGLN
  minTemperature?: number; // LMIVMarkantStockTempMin
  maxTemperature?: number; // LMIVMarkantStockTempMax
  shelfLifeDays?: number; // GHPA00HBK
  contactName?: string; // MarkantcontactName
  contactAddress?: string; // MarkantcontactAddress
  
  // **NEUE FISCH FELDER**
  fangzone?: string; // Fangzone
  fangmethode?: string; // Fangmethode
  scientificName?: string; // WEDLWissenschaftlicherArtname
  productionMethod?: string; // WEDLProduktionsmethode
  thawingInstructions?: string; // WEDLAuftauhinweis
  
  // **GEFAHRGUT FELDER**
  dangerousGoods?: string; // MarkantdangerousGoodsRegulation - Gefahrgut-Status
  
  // **MARKETING FELDER**
  mainImage?: string; // Hauptbild
  backImage?: string; // Rückseitenbild
  labelImage?: string; // Etikettenbild
  marketingMessage?: string; // Marketingbotschaft des Artikels
  
  // **IMAGE STRUCTURE** (for new image handling)
  images?: {
    mainImage?: string;
    backImages?: string[];
    labelImages?: string[];
  };
  
  // **VERTRIEB FELDER**
  salesPrice?: number; // VK-Preis
  
  // **PRODUKTDATEN FELDER**
  wedlDesignation1?: string; // WEDL Bezeichnung 1
  wedlDesignation2?: string; // WEDL Bezeichnung 2
  creditorNumber?: string; // Kreditor-Nummer (moved from Excel Import Fields)
  brand?: string; // Marke (already exists, keeping here for clarity)
  gtin?: string; // GTIN (moved from Excel Import Fields)
  wedlContent?: number; // WEDL Inhalt (Frontend: Inhalt)
  wedlContentUnit?: string; // WEDL Inhaltseinheit (Frontend: Inhalteinheit)
  wedlUnit?: string; // WEDL Einheit (Frontend: Werbeeinheit)
  pieceWeightArticle?: boolean; // Stück Gewichtsartikel (Toggle Switch)
  wedlLine?: string; // WEDL Linie (Frontend: Linie)
  alcoholContent?: number; // Alkoholgehalt (moved from Excel Import Fields)
  basePriceContent?: string; // Grundpreisinhalt
  baseUnit?: string; // Grundeinheit
  baseComparisonUnitCode?: string; // Basis Vergleichs-Einheitencode
  
  // **ERWEITERTE PERFION FELDER**
  // Basic Product Info
  parentId?: string; // _parent
  designationRequired?: boolean; // #WEDLBezeichnung1RQ
  goldenRecord?: boolean; // WEDLGoldenRecord
  productCompletePct?: number; // #ProductCompletePct
  trafficLightStatus?: string; // #WEDLAmpelStatus
  itemType?: string; // ItemType_DE
  itemNumberCheckDigit?: string; // #WEDLPruefzifferArtikelnummer
  
  // Product Details
  productLine?: string; // WEDLLinie_DE
  exchangeInformation?: string; // WEDLAbtauschinformationen
  
  // Dates and Terms
  minTermProduction?: string; // LMIVMarkantMinTermProduction
  minTermIncoming?: string; // LMIVMarkantMinTermIncomming
  minTermOutgoing?: string; // #LMIVMarkantMinTermOutcoming
  
  // Categories and Codes
  gpcCategory?: string; // MarkantgpcCategory_DE
  departmentCode?: string; // WEDLABTABTKZWGRKZ
  unitSwap?: string; // WEDLEinheitenSwap
  barcodeSwap?: string; // WEDLBarcodesSwap
  orderUnitSwap?: string; // MarkantOrderUnitSwap
  
  // Storage
  storageZone?: string; // WEDLLagerzone_DE
  stockTempMin?: number; // LMIVMarkantStockTempMin
  stockTempMax?: number; // LMIVMarkantStockTempMax
  
  // Origin and Labels
  countryOfOrigin?: string; // MarkantCountryOfOrigin
  originCountryTrue?: string; // WEDLHerkunftslandtrue
  primaryIngredient?: string; // WEDLPrimaerzutat_DE
  labels?: string[]; // WEDLLabels_DE (as processed array from Perfion)
  labelsString?: string; // WEDLLabels_DE (als String für direkten Perfion-Import)
  reusableCode?: string; // MarkantonewayReusableCode_DE
  
  // Content and Weight
  netContent?: number; // MarkantnetContent
  netContentUom?: string; // MarkantnetContentUom_DE
  drainedWeight?: number; // MarkantdrainedWeight
  drainedWeightUom?: string; // MarkantdrainedWeightUom_DE
  meatOriginAT?: boolean; // WEDLHerfkuftATFleisch
  
  // Swaps and Content
  nutrientContentSwap?: string; // #MarkantnutrientContentSwap
  allergenTypeSwap?: string; // #MarkantAllergeneTypeSwap
  vePriceUnitConversionFactor?: number; // WEDLVEUmrechungsfaktorPreiseinheit
  targetGroupMarketing?: string; // WEDLZuordnungTargetGroupMarketing_DE
  
  // Approvals and Status (Boolean Toggle Switches)
  ghpApproval?: boolean; // GHPFreigabe
  approvedERP?: boolean; // #ApprovedERP
  approvedPrint?: boolean; // #ApprovedPrint
  approvedWeb?: boolean; // #ApprovedWeb
  bcBlocked?: boolean; // BCBlocked
  bcItemDescription?: string; // #BCItemDescription
  bcSearchDescription?: string; // #BCSearchDescription
  
  // ECR Levels
  ecrLevel1?: string; // #RELWEDLECRECRSGECRSUGECRWGREbene1
  ecrLevel2?: string; // #RELWEDLECRECRSGECRSUGECRWGREbene2
  
  // LMIV Data
  lmivData: LMIVData;
  
  // **NÄHRWERTE (VEREINFACHT FÜR UI)**
  nutritionValues?: {
    [key: string]: number | string | null;
  };
  
  // **EINHEITEN (VEREINFACHT FÜR UI)**
  units?: {
    id?: string;
    unitDescriptor?: string;
    packagingUnit?: string;
    quantityOfBaseUnits?: string;
    purchaseInputUnit?: string;
    salesInputUnit?: string;
    isPieceWeightArticle?: boolean;
    isTradeItemBaseUnit?: boolean;
    width?: string;
    widthUnit?: string;
    height?: string;
    heightUnit?: string;
    depth?: string;
    depthUnit?: string;
    grossWeight?: string;
    grossWeightUnit?: string;
    volumeCM3?: string;
  }[];
  
  // **BARCODES (VEREINFACHT FÜR UI)**
  barcodes?: {
    id?: string;
    gtin?: string;
    unitDescriptor?: string;
    barcodeType?: 'EAN13' | 'EAN8' | 'UPC' | 'CODE128' | 'GTIN14' | 'OTHER';
    isMainBarcode?: boolean;
    isPrintable?: boolean;
  }[];
  
  // **ZUSATZHINWEISE**
  allergens?: {
    [key: string]: {
      contains?: boolean;
      mayContain?: boolean;
    };
  };
  additives?: string;
  consumerHints?: string;
  preparationHints?: string;
  additionalInfo?: string;
  
  // **LMIV & NÄHRWERTE FELDER FÜR UI**
  ingredients?: string; // Zutatenliste
  originCountry?: string; // Herkunftsland
  storageInstructions?: string; // Lager-/Verwendungshinweise
  
  // **NEUE TABELLEN-REFERENZEN**
  unitsDetailed?: IUnit[];
  barcodesDetailed?: IBarcode[];
  ecrCategories?: IECRCategory[];
  labelsDetailed?: ILabel[];
  nutritionalValues?: INutritionalValue[];
  allergensDetailed?: IAllergen[];
  
  // Status & Workflow
  status: ArticleStatus;
  completionPercentage: number;
  
  // Change Tracking
  hasUnviewedChanges?: boolean; // Ob es ungesehene Änderungen gibt
  lastSubmissionDate?: string; // Wann zuletzt vom Lieferant abgeschickt
  changedFields?: string[]; // Liste der geänderten Felder seit letzter Ansicht
  submissionSnapshot?: SupplierArticle; // Snapshot beim Submit für Change-Tracking
  
  // Deadlines
  deadline?: string;
  submittedAt?: string;
  approvedAt?: string;
  
  // Communication
  internalNotes: InternalNote[];
  supplierNotes: SupplierNote[];
  
  // Missing Fields Tracking
  missingFields: string[];
  requestedFields: RequestedField[];
  
  // Audit Trail
  changeLog: ChangeLogEntry[];
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastModifiedBy: string;
  
  // Email Tracking
  lastReminderAt?: string;
  reminderCount: number;
}

// **ARTICLE FILTERS**
export interface ArticleFilters {
  search: string;
  status: string;
  supplierId: string;
  ekCode: string;
  category: string;
  overdue: boolean;
  completionMin: number;
  completionMax: number;
  showDeleted: boolean;
  linkedSuppliersOnly: boolean; // Neue Option für verknüpfte Lieferanten
}

// **ARTICLE STATISTICS**
export interface ArticleStats {
  total: number;
  draft: number;
  inProgress: number;
  submitted: number;
  approved: number;
  overdue: number;
  averageCompletionRate: number;
}

// **BULK OPERATIONS**
export interface BulkCreateArticleData {
  supplierId: string;
  articleNumber: string;
  name: string;
  description?: string;
  category?: string;
  brand?: string;
}

export interface BulkCreateResult {
  successful: Array<{
    articleId: string;
    articleNumber: string;
    supplierName: string;
    supplierId: string;
  }>;
  failed: Array<{
    articleNumber: string;
    supplierName: string;
    error: string;
  }>;
  total: number;
}

// **REQUEST FIELDS DATA**
export interface RequestFieldsData {
  fields: string[];
  comment?: string;
}

// **CHANGE TRACKING INTERFACES**
export interface FieldChange {
  field: string;
  fieldLabel: string;
  oldValue: any;
  newValue: any;
  changedAt: string;
  changedBy: string;
}

export interface ChangeTrackingData {
  hasChanges: boolean;
  changedFields: string[];
  changes: FieldChange[];
  lastViewedAt?: string;
}

// **FIELD COMPARISON RESULT**
export interface FieldComparisonResult {
  hasChanges: boolean;
  changes: {
    [fieldPath: string]: {
      oldValue: any;
      newValue: any;
      fieldLabel: string;
    };
  };
}

// **API RESPONSES**
export interface ArticlesResponse {
  articles: SupplierArticle[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// **SUPPLIER PORTAL ARTICLE VIEW**
export interface SupplierPortalArticle {
  _id: string;
  articleNumber: string;
  supplierArticleNumber?: string;
  name: string;
  description?: string;
  category?: string;
  brand?: string;
  status: ArticleStatus;
  completionPercentage: number;
  deadline?: string;
  lmivData: LMIVData;
  missingFields: string[];
  requestedFields: RequestedField[];
  supplierNotes: SupplierNote[];
  lastReminderAt?: string;
  createdAt: string;
  updatedAt: string;
}

// **FIELD VALIDATION RULES**
export interface FieldValidation {
  field: string;
  label: string;
  required: boolean;
  type: 'text' | 'number' | 'array' | 'object';
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export const LMIV_FIELD_VALIDATIONS: FieldValidation[] = [
  {
    field: 'lmivData.ingredients',
    label: 'Zutaten',
    required: true,
    type: 'text'
  },
  {
    field: 'lmivData.allergens',
    label: 'Allergene',
    required: true,
    type: 'array'
  },
  {
    field: 'lmivData.nutritionalValues.energy.kcal',
    label: 'Energiewert (kcal)',
    required: true,
    type: 'number',
    validation: { min: 0, max: 9999 }
  },
  {
    field: 'lmivData.nutritionalValues.fat.total',
    label: 'Fett gesamt (g)',
    required: true,
    type: 'number',
    validation: { min: 0, max: 100 }
  },
  {
    field: 'lmivData.nutritionalValues.carbohydrates.total',
    label: 'Kohlenhydrate gesamt (g)',
    required: true,
    type: 'number',
    validation: { min: 0, max: 100 }
  },
  {
    field: 'lmivData.nutritionalValues.protein',
    label: 'Eiweiß (g)',
    required: true,
    type: 'number',
    validation: { min: 0, max: 100 }
  },
  {
    field: 'lmivData.nutritionalValues.salt',
    label: 'Salz (g)',
    required: true,
    type: 'number',
    validation: { min: 0, max: 100 }
  }
];

// **MARKETING FIELD VALIDATIONS**
export const MARKETING_FIELD_VALIDATIONS: FieldValidation[] = [
  {
    field: 'mainImage',
    label: 'Hauptbild',
    required: false,
    type: 'text'
  },
  {
    field: 'backImage',
    label: 'Rückseitenbild',
    required: false,
    type: 'text'
  },
  {
    field: 'labelImage',
    label: 'Etikettenbild',
    required: false,
    type: 'text'
  },
  {
    field: 'marketingMessage',
    label: 'Marketingbotschaft des Artikels',
    required: false,
    type: 'text'
  }
];

// **SALES FIELD VALIDATIONS**
export const SALES_FIELD_VALIDATIONS: FieldValidation[] = [
  {
    field: 'salesPrice',
    label: 'VK-Preis',
    required: false,
    type: 'number',
    validation: { min: 0 }
  }
];

// **PRODUCT DATA FIELD VALIDATIONS**
export const PRODUCT_DATA_FIELD_VALIDATIONS: FieldValidation[] = [
  {
    field: 'wedlDesignation1',
    label: 'WEDL Bezeichnung 1',
    required: false,
    type: 'text'
  },
  {
    field: 'wedlDesignation2',
    label: 'WEDL Bezeichnung 2',
    required: false,
    type: 'text'
  },
  {
    field: 'creditorNumber',
    label: 'Kreditor-Nummer',
    required: false,
    type: 'text'
  },
  {
    field: 'brand',
    label: 'Marke',
    required: false,
    type: 'text'
  },
  {
    field: 'gtin',
    label: 'GTIN',
    required: false,
    type: 'text'
  },
  {
    field: 'wedlContent',
    label: 'Inhalt',
    required: false,
    type: 'number',
    validation: { min: 0 }
  },
  {
    field: 'wedlContentUnit',
    label: 'Inhalteinheit',
    required: false,
    type: 'text'
  },
  {
    field: 'wedlUnit',
    label: 'Werbeeinheit',
    required: false,
    type: 'text'
  },
  {
    field: 'pieceWeightArticle',
    label: 'Stück Gewichtsartikel',
    required: false,
    type: 'text'
  },
  {
    field: 'wedlLine',
    label: 'Linie',
    required: false,
    type: 'text'
  },
  {
    field: 'alcoholContent',
    label: 'Alkoholgehalt',
    required: false,
    type: 'number',
    validation: { min: 0, max: 100 }
  },
  {
    field: 'basePriceContent',
    label: 'Grundpreisinhalt',
    required: false,
    type: 'text'
  },
  {
    field: 'baseUnit',
    label: 'Grundeinheit',
    required: false,
    type: 'text'
  },
  {
    field: 'baseComparisonUnitCode',
    label: 'Basis Vergleichs-Einheitencode',
    required: false,
    type: 'text'
  }
];

// **STATUS TRANSLATIONS**
export const ARTICLE_STATUS_LABELS: Record<ArticleStatus, string> = {
  [ArticleStatus.DRAFT]: 'Entwurf',
  [ArticleStatus.NEW]: 'Neu',
  [ArticleStatus.REQUESTED]: 'Angefordert',
  [ArticleStatus.IN_PROGRESS]: 'In Bearbeitung',
  [ArticleStatus.SUBMITTED]: 'Eingereicht',
  [ArticleStatus.APPROVED]: 'Genehmigt',
  [ArticleStatus.COMPLETED]: 'Vollständig',
  [ArticleStatus.REJECTED]: 'Abgelehnt',
  [ArticleStatus.NEEDS_CORRECTION]: 'Korrektur erforderlich'
};

// **STATUS TRANSLATIONS FÜR LIEFERANTEN**
export const ARTICLE_STATUS_LABELS_SUPPLIER: Record<ArticleStatus, string> = {
  [ArticleStatus.DRAFT]: 'Entwurf',
  [ArticleStatus.NEW]: 'Neu',
  [ArticleStatus.REQUESTED]: 'Ausstehend',
  [ArticleStatus.IN_PROGRESS]: 'In Bearbeitung',
  [ArticleStatus.SUBMITTED]: 'Eingereicht',
  [ArticleStatus.APPROVED]: 'Genehmigt',
  [ArticleStatus.COMPLETED]: 'Vollständig',
  [ArticleStatus.REJECTED]: 'Abgelehnt',
  [ArticleStatus.NEEDS_CORRECTION]: 'Korrektur erforderlich'
};

// **STATUS COLORS**
export const ARTICLE_STATUS_COLORS: Record<ArticleStatus, string> = {
  [ArticleStatus.DRAFT]: 'gray',
  [ArticleStatus.NEW]: 'green',
  [ArticleStatus.REQUESTED]: 'purple',
  [ArticleStatus.IN_PROGRESS]: 'blue',
  [ArticleStatus.SUBMITTED]: 'orange',
  [ArticleStatus.APPROVED]: 'green',
  [ArticleStatus.COMPLETED]: 'emerald',
  [ArticleStatus.REJECTED]: 'red',
  [ArticleStatus.NEEDS_CORRECTION]: 'yellow'
};
